import * as FileSystem from 'expo-file-system/legacy';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { useSession } from '@/components/auth';
import { useApi } from '@/hooks/use-api';
import { demoDataService } from '@/services/demo-data-service';
import type { Attachment } from '@/types/models';

type UseAttachmentImageResult = {
	data: string | null;
	loading: boolean;
	error: Error | null;
	refresh: () => Promise<void>;
};

type AttachmentLike = Attachment | null | undefined;

// --- Retry configuration ---
const MAX_RETRIES = 3;
const INITIAL_BACKOFF_MS = 1000;

// Longer timeout for image downloads (images can be multi-MB over cellular)
const IMAGE_DOWNLOAD_TIMEOUT_MS = 120000;

// --- In-memory cache (session-lifetime, fast path) ---
const imageCache = new Map<string, string>();
const pendingRequests = new Map<string, Promise<string>>();

// --- Persistent disk cache (survives app restarts on native) ---
const DISK_CACHE_DIR = `${FileSystem.cacheDirectory}attachment-images/`;
let diskCacheDirReady = false;

const ensureDiskCacheDir = async () => {
	if (Platform.OS === 'web' || diskCacheDirReady) return;
	try {
		const info = await FileSystem.getInfoAsync(DISK_CACHE_DIR);
		if (!info.exists) {
			await FileSystem.makeDirectoryAsync(DISK_CACHE_DIR, { intermediates: true });
		}
		diskCacheDirReady = true;
	} catch {
		// If we can't create the dir, disk cache is just unavailable — not fatal
	}
};

const diskCachePath = (storageKey: string): string => {
	// Flatten the storageKey (which contains slashes) into a safe filename
	const safeKey = storageKey.replace(/[^a-zA-Z0-9.-]/g, '_');
	return `${DISK_CACHE_DIR}${safeKey}`;
};

const readFromDiskCache = async (storageKey: string): Promise<string | null> => {
	if (Platform.OS === 'web') return null;
	try {
		await ensureDiskCacheDir();
		const path = diskCachePath(storageKey);
		const info = await FileSystem.getInfoAsync(path);
		if (!info.exists) return null;
		return await FileSystem.readAsStringAsync(path);
	} catch {
		return null;
	}
};

const writeToDiskCache = async (storageKey: string, dataUrl: string): Promise<void> => {
	if (Platform.OS === 'web') return;
	try {
		await ensureDiskCacheDir();
		const path = diskCachePath(storageKey);
		await FileSystem.writeAsStringAsync(path, dataUrl);
	} catch {
		// Non-fatal — memory cache still works
	}
};

const deleteFromDiskCache = async (storageKey: string): Promise<void> => {
	if (Platform.OS === 'web') return;
	try {
		const path = diskCachePath(storageKey);
		await FileSystem.deleteAsync(path, { idempotent: true });
	} catch {
		// Non-fatal
	}
};

// --- Helpers ---

const readBlobAsDataUrl = (blob: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			if (typeof reader.result === 'string') {
				resolve(reader.result);
			} else {
				reject(new Error('Failed to read blob as data URL'));
			}
		};
		reader.onerror = () => {
			reject(new Error('Failed to read blob as data URL'));
		};
		reader.readAsDataURL(blob);
	});
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const isRetryableError = (err: unknown): boolean => {
	if (err instanceof Error) {
		const msg = err.message.toLowerCase();
		return (
			msg.includes('network request failed')
			|| msg.includes('failed to fetch')
			|| msg.includes('load failed')
			|| msg.includes('timeout')
			|| msg.includes('timed out')
			|| msg.includes('aborted')
		);
	}
	return false;
};

// --- Public cache API ---

export const setAttachmentImageCache = (storageKey: string, data: string) => {
	if (!storageKey) {
		return;
	}

	imageCache.set(storageKey, data);
	// Fire-and-forget disk write
	writeToDiskCache(storageKey, data);
};

export const getAttachmentImageFromCache = (storageKey?: string | null): string | null => {
	if (!storageKey) {
		return null;
	}

	return imageCache.get(storageKey) ?? null;
};

export const invalidateAttachmentImage = (storageKey?: string | null) => {
	if (!storageKey) {
		return;
	}

	imageCache.delete(storageKey);
	pendingRequests.delete(storageKey);
	deleteFromDiskCache(storageKey);
};

// --- Hook ---

export const useAttachmentImage = (attachment: AttachmentLike): UseAttachmentImageResult => {
	const { downloadFile } = useApi();
	const { session } = useSession();
	const isDemoMode = session?.isDemoMode ?? false;
	const downloadFileRef = useRef(downloadFile);
	const mountedRef = useRef(true);
	const [data, setData] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const storageKey = attachment?.storageKey;
	const isImage = Boolean(attachment?.isImage);
	const invalidationKey = attachment?.updatedAt ?? attachment?.storageKey ?? attachment?.id ?? '';

	useEffect(() => {
		downloadFileRef.current = downloadFile;
	}, [downloadFile]);

	const setSafeData = useCallback((value: string | null) => {
		if (!mountedRef.current) {
			return;
		}
		setData(value);
	}, []);

	const setSafeLoading = useCallback((value: boolean) => {
		if (!mountedRef.current) {
			return;
		}
		setLoading(value);
	}, []);

	const setSafeError = useCallback((value: Error | null) => {
		if (!mountedRef.current) {
			return;
		}
		setError(value);
	}, []);

	const loadImage = useCallback(async (force?: boolean) => {
		if (!storageKey || !isImage) {
			setSafeData(null);
			setSafeLoading(false);
			setSafeError(null);
			return;
		}

		// 1. Fast path: in-memory cache
		if (!force && imageCache.has(storageKey)) {
			setSafeData(imageCache.get(storageKey) ?? null);
			setSafeLoading(false);
			setSafeError(null);
			return;
		}

		setSafeLoading(true);
		setSafeError(null);

		// 2. Warm path: disk cache (native only) — always checked before network
		try {
			const diskData = await readFromDiskCache(storageKey);
			if (diskData) {
				imageCache.set(storageKey, diskData);
				setSafeData(diskData);
				setSafeLoading(false);
				setSafeError(null);
				return;
			}
		} catch {
			// Disk cache miss — fall through to network
		}

		// 3. Network fetch with deduplication + retry
		let request = pendingRequests.get(storageKey);
		if (!request) {
			request = (async () => {
				// In demo mode, get the data URL directly from local storage
				// This avoids the Blob conversion which doesn't work in React Native
				if (isDemoMode) {
					const dataUrl = await demoDataService.getFileContent(storageKey);
					if (!dataUrl) {
						throw new Error('File not found in local storage');
					}
					setAttachmentImageCache(storageKey, dataUrl);
					return dataUrl;
				}

				// Remote mode: fetch with retry + exponential backoff
				let lastError: Error | null = null;

				for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
					if (attempt > 0) {
						const delay = INITIAL_BACKOFF_MS * Math.pow(2, attempt - 1);
						await sleep(delay);
					}

					try {
						const response = await downloadFileRef.current(
							`/files/${storageKey}`,
							{ timeoutMs: IMAGE_DOWNLOAD_TIMEOUT_MS },
						);
						if (!response.ok) {
							throw new Error(`Failed to load image (${response.status})`);
						}

						const blob = await response.blob();
						const base64 = await readBlobAsDataUrl(blob);
						setAttachmentImageCache(storageKey, base64);
						return base64;
					} catch (err) {
						lastError = err instanceof Error ? err : new Error('Failed to load image');
						// Only retry on transient network errors
						if (!isRetryableError(err) || attempt === MAX_RETRIES) {
							throw lastError;
						}
					}
				}

				throw lastError ?? new Error('Failed to load image');
			})();
			pendingRequests.set(storageKey, request);
		}

		try {
			const base64 = await request;
			setSafeData(base64);
			setSafeError(null);
		} catch (err) {
			const message = err instanceof Error ? err : new Error('Failed to load image');
			setSafeError(message);
			setSafeData(null);
		} finally {
			pendingRequests.delete(storageKey);
			setSafeLoading(false);
		}
	}, [isDemoMode, isImage, setSafeData, setSafeError, setSafeLoading, storageKey]);

	useEffect(() => {
		mountedRef.current = true;
		loadImage();
		return () => {
			mountedRef.current = false;
		};
	}, [loadImage]);

	const refresh = useCallback(async () => {
		await loadImage(true);
	}, [loadImage]);

	return {
		data,
		loading,
		error,
		refresh,
	};
};
